/**
 * English mirror of ServiceKnowledgeLinks.
 * Adds in-cluster hubs, same-category sibling services and knowledge-base
 * links to every /en/services/* page so no page is a dead end.
 * Purely presentational — all targets are existing, verified routes.
 */
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

type Cat = "notary" | "translation" | "legalization" | "marriage" | "visa" | "background";

type Item = { to: string; label: string; hint: string };

const HUBS: Record<Cat, Item[]> = {
  notary: [
    { to: "/en/notary", label: "Notary Public knowledge hub", hint: "How a Thai Notarial Services Attorney works and what they can certify" },
    { to: "/en/notary/faq", label: "Notary Public FAQ", hint: "Real client questions, grouped by topic" },
    { to: "/en/poa/faq", label: "Power of Attorney (POA)", hint: "Land Office forms, stamp duty and overseas use" },
    { to: "/en/consular-legalization/faq", label: "Consular legalization / Apostille", hint: "The step after notarization for use abroad" },
  ],
  translation: [
    { to: "/en/translation/faq", label: "Certified translation FAQ", hint: "Certification of translations and MFA acceptance" },
    { to: "/en/naati", label: "NAATI translation (Australia / NZ)", hint: "Translations accepted by Australian authorities" },
    { to: "/en/interpreter/faq", label: "Court and government interpreters", hint: "Registry, court, Land Office and Immigration" },
    { to: "/en/consular-legalization/faq", label: "Legalization after translation", hint: "What the Department of Consular Affairs requires" },
  ],
  legalization: [
    { to: "/en/consular-legalization/faq", label: "Consular legalization hub", hint: "Legalization Division, Department of Consular Affairs" },
    { to: "/en/translation/faq", label: "Certified translation first", hint: "The step required before legalization" },
    { to: "/en/corporate-docs/faq", label: "Corporate documents", hint: "DBD certificates, affidavits and financial statements abroad" },
    { to: "/en/poa/faq", label: "Cross-border Power of Attorney", hint: "Jurisdiction-by-jurisdiction requirements" },
  ],
  marriage: [
    { to: "/en/marriage/faq", label: "Marriage in Thailand FAQ", hint: "Affirmation of freedom to marry, district office steps" },
    { to: "/en/civil-registry/faq", label: "Civil & family registry", hint: "Kor Ror forms and family-status recording" },
    { to: "/en/translation/faq", label: "Translating marriage documents", hint: "Certified translation for embassies and districts" },
    { to: "/en/police-clearance", label: "Police clearance certificate", hint: "Often requested with spouse visa files" },
  ],
  visa: [
    { to: "/en/visa/faq", label: "Visa & Immigration FAQ", hint: "Thai visas and long-stay reporting" },
    { to: "/en/work-permit/faq", label: "Work permit", hint: "Department of Employment rules and employer conditions" },
    { to: "/en/relocation/faq", label: "Relocation & settling in", hint: "Banking, housing, driving and healthcare" },
    { to: "/en/police-clearance", label: "Police clearance certificate", hint: "Required for many visa and work-permit files" },
  ],
  background: [
    { to: "/en/police-clearance", label: "Police clearance hub", hint: "Royal Thai Police criminal-record check (PCC)" },
    { to: "/en/police-clearance/faq", label: "Police clearance FAQ", hint: "Documents, timelines and applying from abroad" },
    { to: "/en/consular-legalization/faq", label: "Legalizing your PCC", hint: "The follow-up step for overseas use" },
    { to: "/en/employment/faq", label: "Employment & scholarship files", hint: "Document sets that usually include a PCC" },
  ],
};

const KNOWLEDGE: Item[] = [
  { to: "/en/catalog", label: "Full service catalogue", hint: "Search every service in one place" },
  { to: "/en/services", label: "All English service pages", hint: "Pillar guides written for expats" },
  { to: "/en/insights", label: "In-depth guides", hint: "Long-form articles updated with current rules" },
];

export function EnServiceKnowledgeLinks({
  category,
  siblings = [],
}: {
  category: string;
  siblings?: { slug: string; h1: string }[];
}) {
  const hubs = HUBS[(category as Cat) in HUBS ? (category as Cat) : "notary"];
  return (
    <section className="border-b border-border bg-muted/20 py-16">
      <div className="mx-auto max-w-7xl space-y-12 px-4">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-primary md:text-3xl">
            Knowledge hubs for this service
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Keep reading inside the same cluster for documents, step-by-step process and detailed FAQs.
          </p>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {hubs.map((h) => (
              <Link key={h.to} to={h.to} className="group rounded-xl border border-border bg-card p-4 hover:border-gold">
                <h3 className="text-sm font-bold text-primary group-hover:text-gold">{h.label}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{h.hint}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-gold">
                  Read more <ChevronRight className="h-3 w-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {siblings.length > 0 && (
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight text-primary md:text-3xl">
              Other services in the same category
            </h2>
            <div className="mt-5 flex flex-wrap gap-2">
              {siblings.map((s) => (
                <Link
                  key={s.slug}
                  to="/en/services/$service"
                  params={{ service: s.slug }}
                  className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-semibold text-primary hover:border-gold hover:text-gold"
                >
                  {s.h1}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-primary md:text-3xl">
            Knowledge base &amp; tools
          </h2>
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            {KNOWLEDGE.map((k) => (
              <Link key={k.to} to={k.to} className="group rounded-xl border border-border bg-card p-4 hover:border-gold">
                <h3 className="text-sm font-bold text-primary group-hover:text-gold">{k.label}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{k.hint}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}